import { addDays, clockFace, eyebrowDate, formatTime, nextUp, plansForDay, EVERYONE, toUnit } from '@obv/core';
import { usePlanWindow } from '@/queries/hooks';
import { memberLabel, useDisplay } from '../context';
import { WeatherIcon } from '../icons';
import { Card, ScreenHeader } from '../ui';
import { UpdatedPill, useWeatherState } from './weatherState';

/** "Time to slow down": clock, the rest of tonight, and a look at tomorrow. */
export function EveningView() {
  const ctx = useDisplay();
  const { w } = useWeatherState();
  const windowQ = usePlanWindow(ctx.clock.dateKey, ctx.boot.server_time);
  const items = windowQ.data ? windowQ.data.items : [];
  const tonight = plansForDay(items, ctx.clock.dateKey, EVERYONE).filter((p) => !p.all_day && !p.completed && (p.time || '') > ctx.clock.hhmm);
  const tomorrowKey = addDays(ctx.clock.dateKey, 1);
  const tomorrow = plansForDay(items, tomorrowKey, EVERYONE);
  const face = clockFace(ctx.clock.hhmm, ctx.prefs.h24);
  const tw = w && w.daily[1];
  const portrait = ctx.orientation === 'portrait';
  return (
    <>
      {ctx.chromeHidden ? null : <ScreenHeader eyebrow={eyebrowDate(ctx.clock.dateKey)} title="Time to slow down." right={<UpdatedPill />} />}
      <div className={`${ctx.chromeHidden ? '' : 'mt-4'} grid min-h-0 flex-1`} style={{ gridTemplateColumns: portrait ? 'minmax(0,1fr)' : 'minmax(0,1.1fr) minmax(0,1fr)', gridGap: 14 }}>
        <Card tone={5} className="flex flex-col justify-center px-8 py-6">
          <div className="flex items-baseline">
            <div className="tabular font-light leading-none tracking-[-0.04em]" style={{ fontSize: 120 }}>
              {face.main}
            </div>
            {face.suffix ? <div className="ml-3 text-[26px]">{face.suffix}</div> : null}
          </div>
          <div className="mt-4 text-[17px] text-muted">
            {tonight.length ? `${tonight.length} more ${tonight.length === 1 ? 'plan' : 'plans'} tonight` : 'Nothing else tonight. Rest well.'}
          </div>
          <div className="mt-3">
            {tonight.slice(0, 4).map((p) => (
              <div key={p.id} className="flex h-[40px] items-center border-t border-line text-[16px]">
                <span className="tabular w-[80px] text-muted">{formatTime(p.time!, ctx.prefs.h24)}</span>
                <span className="ellipsis flex-1">{p.title}</span>
                <span className="ml-3 text-[13px] text-muted2">{memberLabel(ctx, p.member_id).name}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card tone={6} className="flex flex-col px-7 py-6">
          <div className="text-[12px] font-semibold tracking-eyebrow text-muted">TOMORROW</div>
          {tw ? (
            <div className="mt-4 flex items-center">
              <WeatherIcon kind={tw.kind} size={52} />
              <div className="ml-4">
                <div className="text-[34px] font-light">
                  {toUnit(tw.max_c, ctx.prefs.unit)}° <span className="text-[20px] text-muted">/ {toUnit(tw.min_c, ctx.prefs.unit)}°</span>
                </div>
                {tw.precip_prob >= 20 ? <div className="text-[14px] text-rain">{tw.precip_prob}% chance of rain</div> : null}
              </div>
            </div>
          ) : null}
          <div className="mt-6 text-[15px]">{tomorrow.length ? `First up: ${tomorrow[0]!.title}` : 'An open day tomorrow.'}</div>
          {tomorrow[0] && !tomorrow[0].all_day ? (
            <div className="mt-1 text-[14px] text-muted2">
              {formatTime(tomorrow[0].time!, ctx.prefs.h24)} · {memberLabel(ctx, tomorrow[0].member_id).name}
            </div>
          ) : null}
        </Card>
      </div>
    </>
  );
}

/** "Room to focus": just the clock and the next plan. */
export function FocusView() {
  const ctx = useDisplay();
  const windowQ = usePlanWindow(ctx.clock.dateKey, ctx.boot.server_time);
  const next = windowQ.data ? nextUp(plansForDay(windowQ.data.items, ctx.clock.dateKey, EVERYONE), ctx.clock.hhmm) : undefined;
  const face = clockFace(ctx.clock.hhmm, ctx.prefs.h24);
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="flex items-baseline">
        <div className="tabular font-light leading-none tracking-[-0.05em]" style={{ fontSize: ctx.orientation === 'portrait' ? 160 : 200 }}>
          {face.main}
        </div>
        {face.suffix ? <div className="ml-4 text-[34px]">{face.suffix}</div> : null}
      </div>
      <div className="mt-6 text-[20px] text-muted">{eyebrowDate(ctx.clock.dateKey)}</div>
      <div className="mt-10 text-[22px]">
        {next ? (
          <>
            <span className="text-muted">Next · </span>
            {next.title} <span className="text-muted">at {formatTime(next.time!, ctx.prefs.h24)}</span>
          </>
        ) : (
          <span className="text-muted">Nothing else today.</span>
        )}
      </div>
    </div>
  );
}
